// API Base Configuration

import API_CONFIG from '../config/apiConfig';

class ApiService {
  constructor() {
    this.baseURL = API_CONFIG.BASE_URL;
    this.onTokenExpired = null; // Callback for token expiration
  }

  // Set callback for token expiration
  setTokenExpiredCallback(callback) {
    this.onTokenExpired = callback;
  }

  // Get auth token from localStorage
  getAuthToken() {
    return localStorage.getItem('token');
  }

  // Get auth headers
  getAuthHeaders() {
    const token = this.getAuthToken();
    return {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    };
  }

  // Generic API call method
  async makeRequest(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const config = {
      headers: this.getAuthHeaders(),
      ...options,
    };

    try {
      const response = await fetch(url, config);

      // Handle different response types
      if (response.status === 204) {
        return { success: true };
      }

      const contentType = response.headers.get('content-type');
      let data;

      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        data = await response.text();
      }

      if (!response.ok) {
        // Handle token expiration (401 Unauthorized)
        if (response.status === 401) {
          console.warn('Token expired or unauthorized access');
          if (this.onTokenExpired) {
            this.onTokenExpired();
          }
          throw new Error('Session expired. Please login again.');
        }
        throw new Error(
          data.message || `HTTP error! status: ${response.status}`
        );
      }

      return data;
    } catch (error) {
      console.error('API Request failed:', error);
      throw error;
    }
  }

  // GET binary (blob) - for images/files that require auth headers
  async getBlob(endpoint) {
    const url = `${this.baseURL}${endpoint}`;
    const token = this.getAuthToken();
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    });

    if (!response.ok) {
      const contentType = response.headers.get('content-type');
      let message = `HTTP error! status: ${response.status}`;
      if (contentType && contentType.includes('application/json')) {
        try {
          const err = await response.json();
          message = err.message || message;
        } catch (_) {}
      }
      throw new Error(message);
    }

    return response.blob();
  }

  // GET request
  async get(endpoint) {
    return this.makeRequest(endpoint, { method: 'GET' });
  }

  // POST request
  async post(endpoint, data) {
    return this.makeRequest(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.getAuthToken()}`,
      },
    });
  }

  // PUT request
  async put(endpoint, data) {
    return this.makeRequest(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // PATCH request
  async patch(endpoint, data) {
    return this.makeRequest(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  // DELETE request
  async delete(endpoint) {
    return this.makeRequest(endpoint, { method: 'DELETE' });
  }

  // File upload request
  async uploadFile(endpoint, formData) {
    const token = this.getAuthToken();
    const url = `${this.baseURL}${endpoint}`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),

      },
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(
        error.message || `HTTP error! status: ${response.status}`
      );
    }

    return response.json();
  }

  async postMultipart(endpoint, jsonData, file) {
    const formData = new FormData();
  
    // Wrap JSON in quotes (matches Postman curl)

    const token = this.getAuthToken();
    formData.append(
      'request',
      new Blob([JSON.stringify(jsonData)], { type: 'application/json' })
    );

    if (file) {
      if (Array.isArray(file)) {
        file.forEach((f) => formData.append('photos', f));
      } else {
        formData.append('photos', file); // single file
      }
    }

    const url = `${this.baseURL}${endpoint}`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),

      },
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(
        error.message || `HTTP error! status: ${response.status}`
      );
    }

    return response.json();
  }
}

const apiServiceInstance = new ApiService();
export default apiServiceInstance;
